import { COPY_POLICIES } from "./copy-policy";
import { richTextDocumentHasContent, validateDocumentSlotLength, validateRichTextDocument, } from "./rich-text-document";
import { validateScalarSlotLength } from "./scalar-slot-validation";
/** How many chapters one Chapters block holds. Not a range — exactly this many. */
export const CHAPTER_COUNT = 3;
export const CHAPTERS_MAX_LENGTHS = {
    eyebrow: 20,
    title: COPY_POLICIES.title.maxLength,
    description: COPY_POLICIES.description.maxLength,
};
const FEATURE = { width: 1000, height: 750 };
const DETAIL = { width: 600, height: 450 };
const SLOT_NAMES = [
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
];
/**
 * The nine slots, in the order an Editor reads them: each chapter's feature
 * picture followed by its two details. Chapter positions are 1-based, so
 * chapter N owns slots 3N-2, 3N-1 and 3N.
 */
export const CHAPTERS_PICTURE_SLOTS = SLOT_NAMES.map((name, index) => {
    const chapter = Math.floor(index / 3) + 1;
    const role = ["Feature", "Detail A", "Detail B"][index % 3];
    const size = index % 3 === 0 ? FEATURE : DETAIL;
    return {
        key: `media${name}`,
        label: `Chapter ${chapter} · ${role}`,
        width: size.width,
        height: size.height,
    };
});
/**
 * The three picture slots chapter `position` owns — feature first, then its two
 * details. 1-based.
 *
 * The "chapter N owns slots 3N-2, 3N-1 and 3N" arithmetic above is stated once
 * and read here, so a renderer, a schema and a validator cannot each slice the
 * nine differently.
 */
export function chapterPictureSlots(position) {
    if (position < 1 || position > CHAPTER_COUNT) {
        throw new Error(`No chapter at position ${position}`);
    }
    return CHAPTERS_PICTURE_SLOTS.slice((position - 1) * 3, position * 3);
}
/** Just the keys of {@link chapterPictureSlots}, in the same order. */
export function chapterPictureSlotKeys(position) {
    return chapterPictureSlots(position).map((slot) => slot.key);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * A stored picture reference, or nothing.
 *
 * Anything that is not a usable reference reads as an unfilled slot rather than
 * as a refusal: a slot is allowed to be empty, so there is no rule here to
 * break. Both halves are required — a reference that cannot name its asset
 * cannot be resolved to a picture, and rendering a URL with a hole in it is
 * worse than rendering nothing.
 */
function readPicture(value) {
    if (!isRecord(value))
        return null;
    const { assetId, cropId } = value;
    if (typeof cropId !== "string" || cropId === "")
        return null;
    if (typeof assetId !== "string" || assetId === "")
        return null;
    return { assetId, cropId };
}
/**
 * Validate one chapter.
 *
 * Order matters: the plain fields are checked first, then the description's
 * structural validity, then whether it says anything. A malformed description is
 * reported as malformed rather than probed for text.
 */
function validateChapter(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The chapter is malformed" };
    }
    const { eyebrow, title, description, ctaLabel, ctaHref } = value;
    if (typeof eyebrow !== "string" || eyebrow.trim() === "") {
        return { valid: false, reason: "Every chapter needs an eyebrow" };
    }
    const eyebrowLength = validateScalarSlotLength({ label: "Eyebrow", maxLength: CHAPTERS_MAX_LENGTHS.eyebrow }, eyebrow);
    if (!eyebrowLength.valid)
        return eyebrowLength;
    if (typeof title !== "string" || title.trim() === "") {
        return { valid: false, reason: "Every chapter needs a title" };
    }
    const titleLength = validateScalarSlotLength({ label: "Title", maxLength: CHAPTERS_MAX_LENGTHS.title }, title);
    if (!titleLength.valid)
        return titleLength;
    const label = typeof ctaLabel === "string" ? ctaLabel.trim() : "";
    const href = typeof ctaHref === "string" ? ctaHref.trim() : "";
    if ((label === "") !== (href === "")) {
        return {
            valid: false,
            reason: "A chapter's call to action needs both a label and a link",
        };
    }
    const validation = validateRichTextDocument(description);
    if (!validation.valid) {
        return { valid: false, reason: validation.reason };
    }
    if (!richTextDocumentHasContent(validation.document)) {
        return { valid: false, reason: "Every chapter needs a description" };
    }
    const descriptionLength = validateDocumentSlotLength({ label: "Description", maxLength: CHAPTERS_MAX_LENGTHS.description }, validation.document);
    if (!descriptionLength.valid)
        return descriptionLength;
    return {
        valid: true,
        chapter: {
            eyebrow,
            title,
            description: validation.document,
            ctaLabel: label,
            ctaHref: href,
        },
    };
}
/** Validate a whole Chapters block. */
export function validateChapters(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Chapters block is malformed" };
    }
    const { chapters, pictures } = value;
    if (!Array.isArray(chapters) || chapters.length !== CHAPTER_COUNT) {
        return {
            valid: false,
            reason: `Chapters holds exactly ${CHAPTER_COUNT} chapters`,
        };
    }
    const validated = [];
    for (const chapter of chapters) {
        const result = validateChapter(chapter);
        if (!result.valid)
            return { valid: false, reason: result.reason };
        validated.push(result.chapter);
    }
    // Every declared slot appears, so a caller reading one back never has to tell
    // "no picture" apart from "no such slot".
    const held = isRecord(pictures) ? pictures : {};
    const readPictures = {};
    for (const slot of CHAPTERS_PICTURE_SLOTS) {
        readPictures[slot.key] = readPicture(held[slot.key]);
    }
    return {
        valid: true,
        content: { chapters: validated, pictures: readPictures },
    };
}
//# sourceMappingURL=chapters.js.map